// =============================================================================
// components/discover/CommunityPreviewModal.tsx — compact bottom-sheet preview
// shown before joining a community/server (reference IMG_5873: avatar, name,
// member avatar-stack + count, description, one primary CTA).
//
// Pattern reuse: same Modal + backdrop-Pressable + GlassSheet construction as
// components/moderation/ReportBlockSheet.tsx (the app's existing bottom-sheet
// pattern) — recolored to nothing, it's already on the lavender glass system.
//
// Generic on purpose: CommunitiesScreen passes communities (join = POST
// /communities/:name/join) and DiscoverServersScreen passes servers (join =
// POST /servers/:id/join) — the join call itself stays in the owning screen,
// this sheet only renders + dispatches onJoin/onView.
// =============================================================================
import React from 'react';
import { Modal, View, Text, Pressable, StyleSheet } from 'react-native';
import GlassSheet from '../glass/GlassSheet';
import GlassButton from '../glass/GlassButton';
import Avatar from '../ui/Avatar';
import AvatarStack from './AvatarStack';
import { colors, spacing } from '../../constants/theme';
import { fonts } from '../../constants/theme';

export interface PreviewTarget {
  /** Stable key (community name / server id) — also the join-endpoint param. */
  key: string;
  title: string;
  /** Small handle line under the title (e.g. "c/defi-degens"). */
  handle?: string;
  description?: string;
  avatar?: string;
  memberCount: number;
  joined?: boolean;
}

interface Props {
  visible: boolean;
  target: PreviewTarget | null;
  /** Join in-flight (renders the primary CTA's spinner). */
  joining?: boolean;
  onClose: () => void;
  /** Primary CTA while not joined. */
  onJoin: () => void;
  /** Secondary CTA (and primary once joined) — open the full screen. */
  onView: () => void;
}

export default function CommunityPreviewModal({
  visible,
  target,
  joining = false,
  onClose,
  onJoin,
  onView,
}: Props) {
  if (!target) {
    // Keep the Modal mounted-consistent for the caller (mirrors ReportBlockSheet).
    return <Modal visible={false} transparent onRequestClose={onClose} children={<View />} />;
  }

  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>
      <Pressable style={styles.backdrop} onPress={onClose} accessibilityLabel="Dismiss preview" />
      <View style={styles.sheetWrap} pointerEvents="box-none">
        <GlassSheet>
          {/* Identity row — small logo + handle (reference IMG_5873 top row) */}
          <View style={styles.header}>
            <Avatar uri={target.avatar} name={target.title} size={48} />
            {!!target.handle && (
              <Text style={styles.handle} numberOfLines={1}>{target.handle}</Text>
            )}
          </View>

          {/* Big headline (reference: large two-line title under the logo row) */}
          <Text style={styles.title} numberOfLines={2}>{target.title}</Text>

          {/* Description */}
          {!!target.description && (
            <Text style={styles.description} numberOfLines={4}>{target.description}</Text>
          )}

          {/* Members — avatar stack + bold count, below the copy (reference) */}
          <View style={styles.membersRow}>
            <AvatarStack
              seed={target.key}
              count={target.memberCount ?? 0}
              size={24}
              onDark={false}
            />
          </View>

          {/* CTAs — one primary. Joined flips View up to primary. */}
          <View style={styles.ctaBlock}>
            {target.joined ? (
              <GlassButton
                title="View Community"
                variant="primary"
                size="lg"
                fullWidth
                icon="arrow-forward"
                onPress={onView}
              />
            ) : (
              <>
                <GlassButton
                  title="Join Community"
                  variant="primary"
                  size="lg"
                  fullWidth
                  loading={joining}
                  disabled={joining}
                  onPress={onJoin}
                />
                <View style={{ height: spacing.sm }} />
                <GlassButton
                  title="View"
                  variant="ghost"
                  size="md"
                  fullWidth
                  onPress={onView}
                />
              </>
            )}
          </View>
        </GlassSheet>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(26,26,46,0.35)',
  },
  sheetWrap: {
    position: 'absolute',
    left: spacing.md,
    right: spacing.md,
    bottom: spacing.xl,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
  },
  title: {
    fontSize: 23,
    lineHeight: 29,
    color: colors.ink,
    marginTop: spacing.md,
    ...fonts.extrabold,
  },
  handle: {
    flex: 1,
    fontSize: 14,
    color: colors.ink2,
    ...fonts.semibold,
  },
  membersRow: {
    marginTop: spacing.md,
  },
  description: {
    fontSize: 14,
    lineHeight: 20,
    color: colors.ink2,
    marginTop: spacing.sm,
    ...fonts.regular,
  },
  ctaBlock: {
    marginTop: spacing.lg,
  },
});
